/// <amd-module name="Repos/Views/Settings/Branches/Components/Columns/BranchColumns" />
import * as React from "react";
import { SparseTreeNode } from "Repos/SparseTree/SparseTreeProvider.Types";
import * as Resources from "Repos/Views/Settings/Branches/Resources";
import { IObservableLikeValue, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { css } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import { TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { EnhancedGitRef } from "../../Data/EnhancedGitRef";

interface ColumnGitRef {
    gitRef?: EnhancedGitRef;
    longName: string;
    shortName: string;
}

function getColumnGitRef(columnContent: ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>): ColumnGitRef {
    const sparseNode = ObservableLike.getValue(columnContent.underlyingItem.data);

    return {
        gitRef: sparseNode.kind === "item" ? sparseNode.data : undefined,
        longName: sparseNode.absoluteIdentifier,
        shortName: sparseNode.relativeIdentifier
    };
}

export function NameColumn(pathHasPolicy?: (path: string) => boolean): ITreeColumn<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>> {
    return {
        id: "name",
        columnLayout: TableColumnLayout.singleLinePrefix,
        name: Resources.ColumnBranchHeader,
        hierarchical: true,
        width: new ObservableValue(-100),
        renderCell: (
            rowIndex: number,
            columnIndex: number,
            tableColumn: ITreeColumn<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>,
            treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>
        ) => {
            const columnGitRef = getColumnGitRef(treeItem);
            const isFolder = columnGitRef.gitRef === undefined;

            const hasPolicy = pathHasPolicy && pathHasPolicy(columnGitRef.longName);
            let contents: JSX.Element;
            const nameContents = (
                <Tooltip text={columnGitRef.longName}>
                    <span className="text-ellipsis">{columnGitRef.shortName}</span>
                </Tooltip>
            );

            if (columnGitRef.gitRef) {
                const gitRef = columnGitRef.gitRef;

                contents = (
                    <>
                        {nameContents}
                        {gitRef.rootRef.isLocked && (
                            <Icon
                                className="icon-margin"
                                iconName="Lock"
                                tooltipProps={{
                                    text: format(Resources.LockedBy, gitRef.rootRef.isLockedBy.displayName)
                                }}
                            />
                        )}
                        {hasPolicy && (
                            <Icon
                                className="icon-margin"
                                iconName="Ribbon"
                                tooltipProps={{
                                    text: Resources.BranchHasPolicies
                                }}
                            />
                        )}
                        <PillGroup className={"repos-branch-pill-group"} overflow={PillGroupOverflow.fade}>
                            {gitRef.isDefault && (
                                <Pill excludeFocusZone={true} excludeTabStop={true} size={PillSize.compact}>
                                    {Resources.DefaultBranch}
                                </Pill>
                            )}
                            {gitRef.isCompare && (
                                <Pill excludeFocusZone={true} excludeTabStop={true} size={PillSize.compact}>
                                    {Resources.CompareBranch}
                                </Pill>
                            )}
                        </PillGroup>
                    </>
                );
            } else {
                const icon = (
                    <Icon
                        iconName="Ribbon"
                        tooltipProps={{
                            text: Resources.BranchHasPolicies
                        }}
                    />
                );

                contents = (
                    <>
                        {nameContents}
                        {hasPolicy && icon}
                    </>
                );
            }

            return (
                <ExpandableTreeCell
                    contentClassName={css(!isFolder && "bolt-table-cell-content-with-link")}
                    colspan={isFolder ? 7 : undefined}
                    columnIndex={columnIndex}
                    key={columnIndex}
                    treeColumn={tableColumn}
                    treeItem={treeItem}
                >
                    <Icon className={css("icon-margin", isFolder && "repos-folder-icon")} iconName={isFolder ? "FabricFolderFill" : "OpenSource"} />
                    {contents}
                </ExpandableTreeCell>
            );
        }
    };
}
